import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule, Location, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { LoginModalComponent } from '../../shared/components/login-modal/login-modal.component';
import {
  ModalComponent,
  SingleSelectComponent,
} from '../../shared/components/primeng';
import { AssetGridComponent } from '../../shared/components/asset-grid/asset-grid.component';
import {
  AssetFilterComponent,
  FilterConfig,
} from '../../shared/components/asset-filter/asset-filter.component';
import { FeatureCardData } from '../../shared/components/feature-card/feature-card.component';
import { NhaContentService } from '../../shared/services/nha-content.service';
import { ToasterService } from '../../services/toaster.service';
import {
  CatalogueApiService,
  extractHackathonMyWorkflowsListFromResponse,
  HackathonMyWorkflowRow,
  HackathonRunDetailsResponse,
  HackathonSubmitError,
  HackathonSubmitResponse,
  LeadershipApiService,
  RolesApiService,
  SandboxApiService,
  SearchCriteria,
} from '@angular-workspace/services';
import { environment } from '../../../environments/environment';
import {
  AssetHeaderComponent,
  AssetHeaderConfig,
} from '../../shared/components/asset-header/asset-header.component';
import {
  attestAndOpen,
  fetchJobResults,
  submitOnnxModel,
  type JobEvaluationResult,
  type OpenedSession,
  type TransferCheckpoint,
  type TransferPhase,
  type TransferResult,
  type UploadStepName,
} from '@datakaveri/tanuh-ratls-verifier';

/** Reference-dataset sandbox leaderboard POST; uses environment `challengeEvaluatorBaseUrl`. */
const REFERENCE_DATASET_SANDBOX_LEADERBOARD_URL = `${environment.challengeEvaluatorBaseUrl}/leaderboards`;

/** Buffer TEE base URL for Onboard Model RA-TLS transfer only; uses environment `bufferTeeUrl`. */
const ONBOARD_MODEL_TEE_URL = environment.bufferTeeUrl;

type OnboardAttestationStepStatus = 'pending' | 'active' | 'done' | 'error';

interface OnboardAttestationStep {
  id: string;
  title: string;
  detail: string;
  status: OnboardAttestationStepStatus;
}

import {
  findHackathonDatasetBySlug,
  findHackathonDatasetForCatalogueItem,
  getCatalogueDatasetDetailLink,
  getReferenceDatasetDetailLink,
  HACKATHON_DATASETS,
  formatProblemStatement,
  HackathonDataset,
  mapCatalogueItemToDatasetDetailView,
} from './hackathon-datasets.data';
import {
  SUBMIT_MODEL_ROUTE,
  submitModelQueryParams,
} from '../submit-model/submit-model-navigation.util';
import { resolveMetricKeyLabel } from '../../shared/utils/tanuh-dataset-item-payload.util';

export type { HackathonDataset } from './hackathon-datasets.data';
export { HACKATHON_DATASETS } from './hackathon-datasets.data';

export interface PrizeBreakdown {
  rank: string;
  amount: string;
}

export interface ProblemStatement {
  id: number;
  tag: string;
  title: string;
  description: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  domain: string;
  prizes: PrizeBreakdown[];
  /** URL to the full problem statement on the NHA website */
  externalUrl?: string;
}

export interface MasterClass {
  id: number;
  title: string;
  speaker: string;
  designation: string;
  organisation: string;
  date: string;
  time: string;
  mode: string;
  description: string;
  tags: string[];
}

// ── Static fallback data ──────────────────────────────────────────────────────
// Used when the Strapi API is unavailable or returns an empty response.

export const STATIC_PROBLEM_STATEMENTS: ProblemStatement[] = [
  {
    id: 1,
    tag: 'PS-01',
    title: 'Clinical Document Classification & Compliance to STG Requirements',
    description:
      'Automatically classify healthcare documents and perform multilingual OCR on low-quality scans to extract structured, clinical, administrative, and billing data with confidence scores and provenance.',
    difficulty: 'Advanced',
    domain: 'NLP / OCR',
    prizes: [
      { rank: 'Winner 1', amount: '₹ 5,00,000' },
      { rank: 'Winner 2', amount: '₹ 3,00,000' },
      { rank: 'Winner 3', amount: '₹ 2,00,000' },
    ],
    externalUrl: 'https://nha.gov.in/problemStatement1',
  },
  {
    id: 2,
    tag: 'PS-02',
    title: 'Radiological Image-Based Condition Detection & Report Correlation',
    description:
      'Design an assistive solution that can read and interpret medical images to help adjudicators: Understand what the image itself appears to show, Correlate the image findings with the written report submitted by the hospital, and Verify whether both are consistent with the claimed condition, stage, and timeline, as expected under STGs.',
    difficulty: 'Advanced',
    domain: 'Computer Vision / Radiology AI',
    prizes: [
      { rank: 'Winner 1', amount: '₹ 5,00,000' },
      { rank: 'Winner 2', amount: '₹ 3,00,000' },
      { rank: 'Winner 3', amount: '₹ 2,00,000' },
    ],
    externalUrl: 'https://nha.gov.in/problemStatement2',
  },
  {
    id: 3,
    tag: 'PS-03',
    title: 'Document Forgery / Deepfake Detection',
    description:
      'Detect tampering, manipulation, or forgery in submitted documents. Identify altered text, mismatched fonts, inconsistent metadata, detect signature/seal duplications, identify image splicing or erased/overwritten fields.',
    difficulty: 'Advanced',
    domain: 'Forensics / Anomaly Detection',
    prizes: [
      { rank: 'Winner 1', amount: '₹ 5,00,000' },
      { rank: 'Winner 2', amount: '₹ 3,00,000' },
      { rank: 'Winner 3', amount: '₹ 2,00,000' },
    ],
    externalUrl: 'https://nha.gov.in/problemStatement3',
  },
];

const STATIC_MASTER_CLASSES: MasterClass[] = [
  {
    id: 1,
    title: 'Introduction to Auto-Adjudication & AB PMJAY Data Ecosystem',
    speaker: 'Dr. Basant Garg',
    designation: 'CEO',
    organisation: 'National Health Authority',
    date: '8th May 2026',
    time: '09:00 – 10:00 AM',
    mode: 'In-Person',
    description:
      'An overview of the AB PMJAY scheme, the data ecosystem powering it, and the challenges and opportunities in automating claims adjudication at scale.',
    tags: ['Keynote', 'Healthcare', 'Policy'],
  },
  {
    id: 2,
    title: 'Machine Learning Approaches for Claims Processing',
    speaker: 'Prof. Chiranjib Bhattacharyya',
    designation: 'Professor, Dept. of Computer Science',
    organisation: 'Indian Institute of Science (IISc)',
    date: '8th May 2026',
    time: '10:30 – 12:00 PM',
    mode: 'In-Person',
    description:
      'Deep dive into supervised and unsupervised ML techniques applicable to health insurance claims — covering feature engineering, model selection, and evaluation strategies specific to healthcare data.',
    tags: ['Machine Learning', 'Healthcare', 'Technical'],
  },
  {
    id: 3,
    title: 'NLP in Clinical Settings: From Notes to Codes',
    speaker: 'Dr. Sunita Sarawagi',
    designation: 'Professor',
    organisation: 'IIT Bombay',
    date: '8th May 2026',
    time: '02:00 – 03:30 PM',
    mode: 'In-Person',
    description:
      'Practical workshop on applying transformer-based NLP models to clinical text, covering entity recognition, medical concept extraction, and automated ICD coding from unstructured clinical narratives.',
    tags: ['NLP', 'Clinical AI', 'Workshop'],
  },
  {
    id: 4,
    title: 'Responsible AI & Explainability in Healthcare',
    speaker: 'Dr. Pradeep Shenoy',
    designation: 'Research Scientist',
    organisation: 'Google DeepMind India',
    date: '9th May 2026',
    time: '09:30 – 11:00 AM',
    mode: 'In-Person',
    description:
      'Understanding the ethical and regulatory considerations in deploying AI for healthcare decisions, with hands-on examples of SHAP, LIME, and counterfactual explanations for claims adjudication models.',
    tags: ['Responsible AI', 'XAI', 'Ethics'],
  },
  {
    id: 5,
    title: 'Fraud Detection at Scale: Lessons from the Field',
    speaker: 'Shri Deepak Arora',
    designation: 'Director, Analytics',
    organisation: 'National Health Authority',
    date: '9th May 2026',
    time: '11:30 AM – 01:00 PM',
    mode: 'In-Person',
    description:
      'A practitioner session sharing real-world experience in building fraud detection systems for government health schemes — including data challenges, model deployment pipelines, and outcome measurement.',
    tags: ['Fraud Detection', 'Government', 'Practitioner'],
  },
];

@Component({
  selector: 'app-reference-datasets',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    FooterComponent,
    LoginModalComponent,
    ModalComponent,
    SingleSelectComponent,
    AssetGridComponent,
    AssetFilterComponent,
    AssetHeaderComponent,
  ],
  templateUrl: './reference-datasets.component.html',
  styleUrl: './reference-datasets.component.scss',
  host: {
    '[class.embed-onboard-only]': 'embedOnboardOnly',
  },
})
export class ReferenceDatasetsComponent
  implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
  /** When true, only the submit-model form is rendered (no listing chrome). */
  @Input() embedOnboardOnly = false;
  /** Opens the submit form for this hackathon dataset key when embed mode is active. */
  @Input() autoOpenDatasetKey: string | null = null;
  /** Optional display label for the preselected dataset. */
  @Input() autoOpenDatasetLabel: string | null = null;
  @Output() onboardHostClosed = new EventEmitter<void>();

  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Benchmarking Datasets' },
    ],
    title: 'Benchmarking Datasets',
    description:
      'Choose a dataset to infer your model on. Each dataset has been carefully labelled, with each label requiring a consensus between medical practitioners before approval.',
  };

  private readonly platformId = inject(PLATFORM_ID);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly evaluateCooldownStoragePrefix =
    'nha-hackathon-evaluate-cooldown:';
  private cooldownTicker: ReturnType<typeof setInterval> | null = null;
  private nowMs = Date.now();
  activeSection: 'problems' | 'masterclasses' = 'problems';

  isLoading = true;
  isListingLoading = true;
  private listingSearchRequestId = 0;
  loadError = false;
  showLoginModal = false;
  isSandboxRedirecting = false;
  redirectingSandboxProblemId: number | null = null;
  isSandboxEvaluateSubmitting = false;
  sandboxEvaluatingDatasetId: string | null = null;
  showSandboxEvaluateSuccessModal = false;
  sandboxEvaluateSuccessContext: { datasetTitle: string; pvc: string } | null =
    null;
  isEvaluateSubmitting = false;
  evaluatingProblemId: number | null = null;
  isViewEvaluationLoading = false;
  viewingEvaluationProblemId: number | null = null;
  showEvaluateModal = false;
  showNotebookRunningModal = false;
  showNoEvaluationModal = false;
  showMyEvaluationsModal = false;
  runningNotebookName = '';
  isNotebookListNavigating = false;
  pendingEvaluationProblem: ProblemStatement | null = null;
  myWorkflowRows: HackathonMyWorkflowRow[] = [];
  expandedWorkflowId: string | null = null;
  workflowLogsById: Record<string, string> = {};
  workflowLogsLoadingById: Record<string, boolean> = {};

  problemStatements: ProblemStatement[] = [];
  masterClasses: MasterClass[] = [];
  datasets: HackathonDataset[] = HACKATHON_DATASETS;
  catalogueListingAssets: FeatureCardData[] = [];
  listingSearchQuery = '';
  listingSortOption = 'new-to-old';
  listingViewMode: 'grid' | 'list' | 'table' = 'grid';
  listingCurrentPage = 1;
  listingItemsPerPage = 6;
  isListingFilterOpen = false;
  filterConfig: FilterConfig = this.buildListingFilterConfig();

  selectedDataset: string | null = null;
  /** Display label for the Dataset set field (e.g. catalogue detail page title). */
  selectedDatasetLabelOverride: string | null = null;
  modelFile: File | null = null;
  modelWeightsFile: File | null = null;
  pythonFile: File | null = null;
  isModelFileBuffering = false;
  isModelWeightsFileBuffering = false;
  isPythonFileBuffering = false;
  modelUploadProgress: number | null = null;
  modelWeightsUploadProgress: number | null = null;
  pythonUploadProgress: number | null = null;
  private modelProgressToken = 0;
  private modelWeightsProgressToken = 0;
  private pythonProgressToken = 0;
  isOnboardSubmitting = false;
  isOnboardTeeVerifying = false;
  onboardTeeVerified = false;
  onboardAttestationAccepted = false;
  onboardStatusMessage = '';
  showOnboardTransferSection = false;
  onboardTransferComplete = false;
  onboardTransferResult: TransferResult | null = null;
  onboardAttestationSteps: OnboardAttestationStep[] = [];
  private onboardTransferCheckpoint: TransferCheckpoint = {};
  onboardJobEvaluationStatus:
    | 'idle'
    | 'pending'
    | 'fetching'
    | 'complete'
    | 'error' = 'idle';
  onboardJobEvaluationResult: JobEvaluationResult | null = null;
  isLoggedIn = false;
  private onboardTeeSession: OpenedSession | null = null;
  private onboardResultsFetchAbort = false;
  private static readonly ONBOARD_RESULTS_POLL_INTERVAL_MS = 5000;
  private static readonly ONBOARD_RESULTS_POLL_MAX_ATTEMPTS = 120;
  /** Per PS: false = successful list fetch and no notebook linked to that PS (logged-in). */
  private psNotebookEligibility: Record<number, boolean | null> = {};
  /** Catalogue UUID lookups for the legacy "Breast Cancer" / "Oral Cancer" picker labels. */
  private onboardDatasetUuidCache = new Map<string, string>();

  constructor(
    private contentService: NhaContentService,
    private router: Router,
    private keycloak: KeycloakService,
    private rolesService: RolesApiService,
    private sandboxApiService: SandboxApiService,
    private leadershipApiService: LeadershipApiService,
    private toasterService: ToasterService,
    private location: Location,
    private catalogueApiService: CatalogueApiService,
  ) {}

  ngOnInit(): void {
    if (this.embedOnboardOnly) {
      void this.tryAutoOpenOnboardModal();
      return;
    }

    this.startCooldownTicker();
    void this.checkLoginStatus();
    void this.loadCatalogueListing();

    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state ?? window.history.state;
    const openOnboardId = state?.['openOnboardForDatasetId'] as
      | string
      | undefined;

    this.contentService.fetchHackathonContent().subscribe({
      next: ({ problemStatements, masterClasses }) => {
        this.problemStatements =
          problemStatements.length > 0
            ? problemStatements
            : STATIC_PROBLEM_STATEMENTS;
        this.masterClasses =
          masterClasses.length > 0 ? masterClasses : STATIC_MASTER_CLASSES;
        this.isLoading = false;
        void this.refreshEvaluateNotebookEligibility();
        if (openOnboardId) {
          this.redirectLegacyOnboardState(openOnboardId);
        }
      },
      error: () => {
        this.problemStatements = STATIC_PROBLEM_STATEMENTS;
        this.masterClasses = STATIC_MASTER_CLASSES;
        this.isLoading = false;
        this.loadError = true;
        void this.refreshEvaluateNotebookEligibility();
        if (openOnboardId) {
          this.redirectLegacyOnboardState(openOnboardId);
        }
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.embedOnboardOnly &&
      (changes['autoOpenDatasetKey'] || changes['autoOpenDatasetLabel'])
    ) {
      void this.tryAutoOpenOnboardModal();
    }
  }

  ngAfterViewInit(): void {
    if (this.embedOnboardOnly) {
      void this.tryAutoOpenOnboardModal();
    }
  }

  ngOnDestroy(): void {
    this.stopCooldownTicker();
    this.cancelOnboardResultsFetch();
  }

  @HostListener('document:visibilitychange')
  onDocumentVisibilityChange(): void {
    if (
      document.visibilityState === 'visible' &&
      this.problemStatements.length > 0
    ) {
      void this.refreshEvaluateNotebookEligibility();
    }
  }

  openProblemStatement(ps: ProblemStatement): void {
    this.router.navigate(['/reference/problem-statement', ps.id]);
  }

  openReferenceDatasetDetail(ds: HackathonDataset, event?: Event): void {
    event?.stopPropagation();
    void this.router.navigate(['/reference/dataset', ds.leaderboardDatasetId]);
  }

  get filteredListingAssets(): FeatureCardData[] {
    const hardcoded = this.applyListingQuery(this.datasets).map((ds) =>
      this.toListingAsset(ds),
    );
    const catalogue = this.applyListingAssetQuery(
      this.catalogueListingAssets.filter(
        (asset) => !this.isHardcodedListingAsset(asset),
      ),
    );
    return [...hardcoded, ...catalogue];
  }

  get pagedListingAssets(): FeatureCardData[] {
    const start = (this.listingCurrentPage - 1) * this.listingItemsPerPage;
    return this.filteredListingAssets.slice(
      start,
      start + this.listingItemsPerPage,
    );
  }

  get listingTotalPages(): number {
    return Math.max(
      1,
      Math.ceil(this.filteredListingAssets.length / this.listingItemsPerPage) ||
        1,
    );
  }

  onListingSearchChange(query: string): void {
    // UI-only search: filter already-loaded datasets in the client.
    this.listingSearchQuery = query ?? '';
    this.listingCurrentPage = 1;
  }

  onListingSortChange(sortOption: string): void {
    this.listingSortOption = sortOption || 'new-to-old';
    this.listingCurrentPage = 1;
  }

  onListingViewModeChange(mode: 'grid' | 'list' | 'table'): void {
    this.listingViewMode = mode === 'table' ? 'grid' : mode;
  }

  onListingPageChange(page: number): void {
    const nextPage = Math.min(Math.max(1, page), this.listingTotalPages);
    this.listingCurrentPage = nextPage;
  }

  onListingItemsPerPageChange(itemsPerPage: number): void {
    this.listingItemsPerPage = itemsPerPage > 0 ? itemsPerPage : 6;
    this.listingCurrentPage = 1;
  }

  onListingFilterChange(filterConfig: FilterConfig): void {
    this.filterConfig = filterConfig;
    this.updateListingFilterCount();
    this.listingCurrentPage = 1;
  }

  onResetListingFilters(): void {
    this.filterConfig.sections.forEach((section) => {
      section.options?.forEach((option) => {
        option.checked = false;
      });
    });
    this.updateListingFilterCount();
    this.listingCurrentPage = 1;
  }

  openListingFilter(): void {
    this.isListingFilterOpen = true;
  }

  closeListingFilter(): void {
    this.isListingFilterOpen = false;
  }

  onListingSubmitModel(asset: FeatureCardData): void {
    const dataset =
      HACKATHON_DATASETS.find(
        (item) => item.id === asset.referenceDatasetKey,
      ) ??
      findHackathonDatasetForCatalogueItem({
        id: asset.id,
        title: asset.title,
      });
    if (dataset) {
      void this.openOnboardModal(dataset, undefined, asset.title);
      return;
    }

    // Catalogue-only datasets after HACKATHON_DATASETS was emptied.
    void this.openOnboardModalWithDatasetName(
      asset.title,
      asset.id || asset.referenceDatasetKey,
    );
  }

  private toListingAsset(ds: HackathonDataset): FeatureCardData {
    return {
      id: ds.leaderboardDatasetId,
      title: ds.title,
      organization: '',
      tags: [],
      shortDescription: ds.description,
      problemStatement: formatProblemStatement(ds.problemStatement),
      modality: ds.modality,
      volume: ds.volume,
      output: ds.output,
      primaryMetric: ds.primaryMetric,
      secondPrimaryMetric: ds.secondPrimaryMetric,
      secondaryMetric: ds.secondaryMetric,
      referenceDatasetKey: ds.id,
      link: getReferenceDatasetDetailLink(ds, 'reference'),
    };
  }

  private applyListingQuery(source: HackathonDataset[]): HackathonDataset[] {
    const query = this.listingSearchQuery.trim().toLowerCase();
    const selectedLabels = (sectionId: string): string[] =>
      this.filterConfig.sections
        .find((section) => section.id === sectionId)
        ?.options?.filter((option) => option.checked)
        .map((option) => option.label) ?? [];

    const modalities = selectedLabels('modality');
    const outputs = selectedLabels('output');
    const formats = selectedLabels('file-format');

    const filtered = source.filter((ds) => {
      if (query) {
        const haystack = [
          ds.title,
          ds.description,
          ds.modality,
          ds.output,
          ds.volume,
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(query)) {
          return false;
        }
      }
      if (modalities.length && !modalities.includes(ds.modality)) {
        return false;
      }
      if (outputs.length && !outputs.includes(ds.output)) {
        return false;
      }
      if (formats.length) {
        const format = ds.publicProfile?.input.format ?? '';
        if (!formats.includes(format)) {
          return false;
        }
      }
      return true;
    });

    const originalIndex = (ds: HackathonDataset): number =>
      HACKATHON_DATASETS.findIndex((item) => item.id === ds.id);

    switch (this.listingSortOption) {
      case 'a-z':
        return [...filtered].sort((a, b) => a.title.localeCompare(b.title));
      case 'z-a':
        return [...filtered].sort((a, b) => b.title.localeCompare(a.title));
      case 'old-to-new':
        return [...filtered].sort(
          (a, b) => originalIndex(b) - originalIndex(a),
        );
      default:
        return [...filtered].sort(
          (a, b) => originalIndex(a) - originalIndex(b),
        );
    }
  }

  private applyListingAssetQuery(source: FeatureCardData[]): FeatureCardData[] {
    const query = this.listingSearchQuery.trim().toLowerCase();
    const selectedLabels = (sectionId: string): string[] =>
      this.filterConfig.sections
        .find((section) => section.id === sectionId)
        ?.options?.filter((option) => option.checked)
        .map((option) => option.label) ?? [];

    const modalities = selectedLabels('modality');
    const outputs = selectedLabels('output');
    const formats = selectedLabels('file-format');

    const filtered = source.filter((asset) => {
      if (query) {
        const haystack = [
          asset.title,
          asset.shortDescription,
          asset.modality,
          asset.output,
          asset.volume,
          asset.fileType,
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(query)) {
          return false;
        }
      }
      if (modalities.length && !modalities.includes(asset.modality ?? '')) {
        return false;
      }
      if (outputs.length && !outputs.includes(asset.output ?? '')) {
        return false;
      }
      if (formats.length) {
        const format = asset.fileType ?? '';
        if (!formats.includes(format)) {
          return false;
        }
      }
      return true;
    });

    switch (this.listingSortOption) {
      case 'a-z':
        return [...filtered].sort((a, b) => a.title.localeCompare(b.title));
      case 'z-a':
        return [...filtered].sort((a, b) => b.title.localeCompare(a.title));
      case 'old-to-new':
        return [...filtered].reverse();
      default:
        return filtered;
    }
  }

  private isHardcodedListingAsset(asset: FeatureCardData): boolean {
    const id = (asset.id ?? '').trim().toLowerCase();
    const title = (asset.title ?? '').trim().toLowerCase();
    const key = (asset.referenceDatasetKey ?? '').trim().toLowerCase();
    return HACKATHON_DATASETS.some((dataset) => {
      const datasetId = dataset.id.toLowerCase();
      const datasetTitle = dataset.title.toLowerCase();
      const datasetSlug = dataset.leaderboardDatasetId.toLowerCase();
      return (
        (key && (key === datasetId || key === datasetSlug)) ||
        (id && (id === datasetId || id === datasetSlug)) ||
        (title && (title === datasetTitle || title === datasetId))
      );
    });
  }

  private async loadCatalogueListing(): Promise<void> {
    await Promise.all([
      this.loadCatalogueDatasets(),
      this.loadListingFilterOptions(),
    ]);
  }

  private async loadCatalogueDatasets(): Promise<void> {
    const requestId = ++this.listingSearchRequestId;
    this.isListingLoading = true;
    this.cdr.markForCheck();
    try {
      // Load catalogue datasets once; search/filter/sort stay UI-only.
      const response = await this.catalogueApiService.searchAllAssets(
        '',
        ['Dataset'],
        [],
        1,
        50,
        'itemCreatedAt:desc;',
        [],
      );
      if (requestId !== this.listingSearchRequestId) {
        return;
      }
      const items = this.extractItemsFromResponse(response);
      this.catalogueListingAssets = items
        .filter(
          (item): item is Record<string, unknown> =>
            !!item && typeof item === 'object' && !Array.isArray(item),
        )
        .map((item) => this.mapCatalogueItemToListingAsset(item));
      try {
        this.mergeListingFilterOptionsFromAssets(this.catalogueListingAssets);
      } catch (error) {
        console.error('Failed to merge catalogue filter options:', error);
      }
    } catch (error) {
      if (requestId !== this.listingSearchRequestId) {
        return;
      }
      console.error(
        'Failed to load catalogue datasets for reference listing:',
        error,
      );
      this.catalogueListingAssets = [];
    } finally {
      if (requestId === this.listingSearchRequestId) {
        this.isListingLoading = false;
        this.cdr.detectChanges();
      }
    }
  }

  private async loadListingFilterOptions(): Promise<void> {
    try {
      const searchCriteria: SearchCriteria[] = [
        {
          searchType: 'term',
          field: 'type',
          values: ['adex:DataBank'],
        },
      ];
      const response = await this.catalogueApiService.getFilterList(
        searchCriteria,
        ['fileFormat', 'accessPolicy'],
        { isToasterRequired: false, isLoaderRequired: false },
      );
      const result = response?.result ?? response?.data ?? response;
      if (!result) {
        return;
      }
      this.mergeListingFilterOption(
        'file-format',
        this.extractListValues(result, 'fileFormat'),
      );
      this.cdr.detectChanges();
    } catch (error) {
      console.error(
        'Failed to load catalogue filter list for reference listing:',
        error,
      );
    }
  }

  private extractItemsFromResponse(response: any): any[] {
    if (!response) {
      return [];
    }
    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response.result)) {
      return response.result;
    }
    if (Array.isArray(response.result?.results)) {
      return response.result.results;
    }
    if (Array.isArray(response.result?.items)) {
      return response.result.items;
    }
    if (Array.isArray(response.results)) {
      return response.results;
    }
    if (Array.isArray(response.data)) {
      return response.data;
    }
    if (Array.isArray(response.data?.result)) {
      return response.data.result;
    }
    if (Array.isArray(response.data?.results)) {
      return response.data.results;
    }
    if (Array.isArray(response.items)) {
      return response.items;
    }

    return [];
  }

  private mapCatalogueItemToListingAsset(
    item: Record<string, unknown>,
  ): FeatureCardData {
    const matched = findHackathonDatasetForCatalogueItem({
      id: item['id'] as string | undefined,
      label: item['label'] as string | undefined,
      name: item['name'] as string | undefined,
      title: item['title'] as string | undefined,
    });
    if (matched) {
      return this.toListingAsset(matched);
    }

    const view = mapCatalogueItemToDatasetDetailView(item);
    const fileFormat = item['fileFormat'] ?? item['format'];
    return {
      id: view.id,
      title: view.title,
      organization: String(
        item['provider'] ?? item['organization'] ?? item['provider_name'] ?? '',
      ),
      tags: [],
      shortDescription: view.description,
      problemStatement: view.problemStatement,
      modality: view.modality,
      volume: view.volume,
      output: view.output,
      primaryMetric: view.primaryMetric,
      secondPrimaryMetric: view.secondPrimaryMetric,
      secondaryMetric: view.secondaryMetric,
      fileType:
        fileFormat != null && String(fileFormat).trim()
          ? String(fileFormat)
          : undefined,
      link: getCatalogueDatasetDetailLink(view.id, 'reference'),
    };
  }

  private mergeListingFilterOptionsFromAssets(assets: FeatureCardData[]): void {
    this.mergeListingFilterOption(
      'modality',
      assets.map((asset) => asset.modality ?? ''),
    );
    this.mergeListingFilterOption(
      'output',
      assets.map((asset) => asset.output ?? ''),
    );
    this.mergeListingFilterOption(
      'file-format',
      assets.map((asset) => asset.fileType ?? ''),
    );
  }

  private mergeListingFilterOption(sectionId: string, values: string[]): void {
    const section = this.filterConfig.sections.find(
      (item) => item.id === sectionId,
    );
    if (!section) {
      return;
    }
    const options = [...(section.options ?? [])];
    const seen = new Set(options.map((option) => option.label.toLowerCase()));
    for (const value of values) {
      const label = value.trim();
      if (!label || label === '—' || seen.has(label.toLowerCase())) {
        continue;
      }
      seen.add(label.toLowerCase());
      options.push({
        id: label
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, ''),
        label,
        checked: false,
      });
    }
    section.options = options;
  }

  private extractListValues(data: unknown, field: string): string[] {
    if (!data) {
      return [];
    }
    if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'string') {
      return [...new Set(data.filter((value): value is string => !!value))];
    }
    if (Array.isArray(data)) {
      const values: string[] = [];
      data.forEach((item) => {
        if (!item || typeof item !== 'object') {
          return;
        }
        const value = field.split('.').reduce<unknown>((current, part) => {
          if (!current || typeof current !== 'object') {
            return undefined;
          }
          return (current as Record<string, unknown>)[part];
        }, item);
        if (Array.isArray(value)) {
          values.push(
            ...value
              .filter((entry) => entry != null && entry !== '')
              .map(String),
          );
        } else if (value != null && value !== '') {
          values.push(String(value));
        }
      });
      return [...new Set(values)];
    }
    if (typeof data === 'object') {
      const value = field.split('.').reduce<unknown>((current, part) => {
        if (!current || typeof current !== 'object') {
          return undefined;
        }
        return (current as Record<string, unknown>)[part];
      }, data);
      if (Array.isArray(value)) {
        return [
          ...new Set(
            value.filter((entry) => entry != null && entry !== '').map(String),
          ),
        ];
      }
      if (value != null && value !== '') {
        return [String(value)];
      }
    }
    return [];
  }

  private buildListingFilterConfig(): FilterConfig {
    return {
      sections: [
        {
          id: 'modality',
          title: 'Modality',
          type: 'checkbox',
          searchPlaceholder: 'Search for modality',
          isExpanded: false,
          icon: '/filters/file-digit.svg',
          options: this.uniqueFilterOptions(
            HACKATHON_DATASETS.map((ds) => ds.modality),
          ),
        },
        {
          id: 'output',
          title: 'Output',
          type: 'checkbox',
          searchPlaceholder: 'Search for output',
          isExpanded: false,
          icon: '/filters/lock.svg',
          options: this.uniqueFilterOptions(
            HACKATHON_DATASETS.map((ds) => ds.output),
          ),
        },
        {
          id: 'file-format',
          title: 'File format',
          type: 'checkbox',
          searchPlaceholder: 'Search for file format',
          isExpanded: false,
          icon: '/filters/file-digit.svg',
          options: this.uniqueFilterOptions(
            HACKATHON_DATASETS.map(
              (ds) => ds.publicProfile?.input.format ?? '',
            ).filter(Boolean),
          ),
        },
      ],
      activeFilterCount: 0,
    };
  }

  private uniqueFilterOptions(
    values: string[],
  ): { id: string; label: string; checked: boolean }[] {
    const seen = new Set<string>();
    const options: { id: string; label: string; checked: boolean }[] = [];
    for (const value of values) {
      const label = value.trim();
      const key = label.toLowerCase();
      if (!label || seen.has(key)) {
        continue;
      }
      seen.add(key);
      options.push({
        id: key.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
        label,
        checked: false,
      });
    }
    return options;
  }

  private updateListingFilterCount(): void {
    let count = 0;
    this.filterConfig.sections.forEach((section) => {
      count += section.options?.filter((option) => option.checked).length ?? 0;
    });
    this.filterConfig.activeFilterCount = count;
  }

  get isOnboardFormValid(): boolean {
    return (
      this.onboardTeeVerified &&
      this.onboardAttestationAccepted &&
      !!this.selectedDataset &&
      !!this.modelFile &&
      !!this.modelWeightsFile &&
      !this.isModelFileBuffering &&
      !this.isModelWeightsFileBuffering &&
      !this.isPythonFileBuffering &&
      (!this.requiresPythonFile || !!this.pythonFile)
    );
  }

  get showModelUploadProgress(): boolean {
    return this.modelFile != null && this.modelUploadProgress != null;
  }

  get showModelWeightsUploadProgress(): boolean {
    return (
      this.modelWeightsFile != null && this.modelWeightsUploadProgress != null
    );
  }

  get showPythonUploadProgress(): boolean {
    return this.pythonFile != null && this.pythonUploadProgress != null;
  }

  wizardStep = 1;
  readonly onboardPageSteps: { id: number; label: string; hint: string }[] = [
    { id: 1, label: 'Dataset', hint: 'Benchmarking dataset' },
    { id: 2, label: 'Verify TEE', hint: 'Attestation identity' },
    { id: 3, label: 'Upload model files', hint: 'Model artifacts' },
    { id: 4, label: 'Transfer progress', hint: 'Encrypted submission' },
  ];

  get canGoWizardNext(): boolean {
    if (this.wizardStep === 1) {
      return (
        !!this.selectedDataset?.trim() || !!this.selectedDatasetLabel?.trim()
      );
    }
    if (this.wizardStep === 2) {
      return this.onboardAttestationAccepted;
    }
    return false;
  }

  goWizardNext(): void {
    if (!this.canGoWizardNext) {
      return;
    }
    // Ensure step-1 selection is persisted even when only a label/key was provided.
    if (this.wizardStep === 1 && !this.selectedDataset?.trim()) {
      this.selectedDataset = this.resolveCanonicalOnboardDatasetKey(
        this.autoOpenDatasetKey,
        this.selectedDatasetLabel,
      );
    }
    this.wizardStep += 1;
    if (this.wizardStep === 2) {
      this.resetOnboardTeeVerification();
    }
    this.cdr.markForCheck();
  }

  goWizardBack(): void {
    if (this.wizardStep <= 1 || this.isOnboardSubmitting) {
      return;
    }
    this.wizardStep -= 1;
    if (this.wizardStep === 2) {
      this.resetOnboardTeeVerification();
    }
    this.cdr.markForCheck();
  }

  /** Drop a previous attestation so the verify step opens on a fresh check. */
  private resetOnboardTeeVerification(): void {
    this.onboardTeeVerified = false;
    this.onboardAttestationAccepted = false;
    this.onboardTeeSession = null;
    this.isOnboardTeeVerifying = false;
    this.onboardStatusMessage = '';
  }

  get onboardFormProgress(): number {
    let completed = 0;
    if (this.selectedDataset) completed++;
    if (this.onboardTeeVerified) completed++;
    if (this.onboardAttestationAccepted) completed++;
    if (this.modelFile) completed++;
    if (this.modelWeightsFile) completed++;
    if (this.requiresPythonFile && this.pythonFile) completed++;
    const total = this.requiresPythonFile ? 6 : 5;
    return Math.round((completed / total) * 100);
  }

  get onboardAttestationOidc() {
    return (
      this.onboardTransferResult?.oidc ?? this.onboardTeeSession?.oidc ?? null
    );
  }

  get selectedDatasetLabel(): string | null {
    const override = this.selectedDatasetLabelOverride?.trim();
    if (override) {
      return override;
    }

    if (!this.selectedDataset) {
      return null;
    }

    const match = HACKATHON_DATASETS.find(
      (ds) =>
        ds.id === this.selectedDataset ||
        ds.leaderboardDatasetId === this.selectedDataset,
    );
    if (match?.title) {
      return match.title;
    }

    const canonical = this.resolveCanonicalOnboardDatasetKey(
      this.selectedDataset,
      null,
    );
    if (canonical === 'Breast Cancer' || canonical === 'Oral Cancer') {
      return canonical;
    }

    return this.selectedDataset;
  }

  get onboardDatasetOptions(): { label: string; value: string }[] {
    if (HACKATHON_DATASETS.length > 0) {
      return HACKATHON_DATASETS.map((ds) => ({
        label: ds.title,
        value: ds.id,
      }));
    }

    // Fallback when static reference list is empty (catalogue-driven TANUH).
    return [
      { label: 'Breast Cancer', value: 'Breast Cancer' },
      { label: 'Oral Cancer', value: 'Oral Cancer' },
    ];
  }

  /** Preprocessing script is required whenever that upload is shown. */
  get requiresPythonFile(): boolean {
    return this.showPythonUploadField || this.embedOnboardOnly;
  }

  /** Preprocessing script upload — Oral Cancer only. */
  get showPythonUploadField(): boolean {
    return (
      this.mapTextToOnboardDatasetId(this.selectedDataset) === 2 ||
      this.mapTextToOnboardDatasetId(this.selectedDatasetLabelOverride) === 2
    );
  }

  /**
   * File picker filters for the onboard form.
   * (This is a UI hint; backend upload still validates the payload.)
   */
  get onboardModelFileAccept(): string | null {
    return '.onnx,.py';
  }

  get onboardWeightsFileAccept(): string | null {
    return '.onnx.data,.py';
  }

  get onboardModelFileAcceptLabel(): string {
    const accept = this.onboardModelFileAccept;
    return accept ? accept.split(',').join(', ') : 'Any file';
  }

  get onboardWeightsFileAcceptLabel(): string {
    const accept = this.onboardWeightsFileAccept;
    return accept ? accept.split(',').join(', ') : 'Any file';
  }

  get onboardJobId(): string | null {
    const response = this.onboardTransferResult?.response;
    if (
      typeof response === 'object' &&
      response !== null &&
      'job_id' in response
    ) {
      return String((response as { job_id: unknown }).job_id);
    }
    return null;
  }

  get onboardOidcAudience(): string {
    const aud = this.onboardAttestationOidc?.aud;
    if (Array.isArray(aud)) {
      return aud.join(', ');
    }
    return aud ?? '—';
  }

  get onboardJobQueuePosition(): number {
    const response = this.onboardTransferResult?.response;
    if (typeof response !== 'object' || response === null) {
      return 1;
    }
    const record = response as Record<string, unknown>;
    const position = record['queue_position'] ?? record['position'] ?? 1;
    const parsed = Number(position);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  get onboardJobQueueTotal(): number {
    const response = this.onboardTransferResult?.response;
    if (typeof response !== 'object' || response === null) {
      return 1;
    }
    const record = response as Record<string, unknown>;
    const total = record['queue_total'] ?? record['queue_size'] ?? 1;
    const parsed = Number(total);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  formatOnboardAccuracy(accuracy: number): string {
    return `${(accuracy * 100).toFixed(2)}%`;
  }

  formatOnboardTimestamp(unixSeconds: number | undefined): string {
    if (!unixSeconds) {
      return '—';
    }
    return new Date(unixSeconds * 1000).toLocaleString();
  }

  formatOnboardFileSize(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  async openOnboardModal(
    ds: HackathonDataset,
    event?: Event,
    datasetLabel?: string,
  ): Promise<void> {
    event?.stopPropagation();

    if (!this.embedOnboardOnly) {
      this.navigateToSubmitModelPage(ds, datasetLabel);
      return;
    }

    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.showLoginModal = true;
        this.cdr.markForCheck();
        return;
      }
    } catch {
      this.showLoginModal = true;
      this.cdr.markForCheck();
      return;
    }

    this.selectedDataset = ds.id;
    this.selectedDatasetLabelOverride = datasetLabel?.trim() || ds.title;
    this.onboardStatusMessage = '';
    this.onboardTeeVerified = false;
    this.onboardAttestationAccepted = false;
    this.onboardTeeSession = null;
    this.cdr.markForCheck();
  }

  async openOnboardModalWithDatasetName(
    datasetName: string,
    datasetKey?: string,
  ): Promise<void> {
    const label = datasetName.trim();
    if (!label) {
      await this.openOnboardModalWithoutDataset();
      return;
    }

    if (!this.embedOnboardOnly) {
      const resolved = datasetKey
        ? this.findHackathonDatasetByKey(datasetKey)
        : undefined;
      this.navigateToSubmitModelPage(resolved, label, datasetKey);
      return;
    }

    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.showLoginModal = true;
        this.cdr.markForCheck();
        return;
      }
    } catch {
      this.showLoginModal = true;
      this.cdr.markForCheck();
      return;
    }

    const resolved = datasetKey
      ? this.findHackathonDatasetByKey(datasetKey)
      : undefined;
    this.selectedDataset =
      resolved?.id ??
      this.resolveCanonicalOnboardDatasetKey(datasetKey, label) ??
      datasetKey?.trim() ??
      label;
    this.selectedDatasetLabelOverride = label;
    this.onboardStatusMessage = '';
    this.onboardTeeVerified = false;
    this.onboardAttestationAccepted = false;
    this.onboardTeeSession = null;
    this.cdr.markForCheck();
  }

  async openOnboardModalWithoutDataset(): Promise<void> {
    if (!this.embedOnboardOnly) {
      this.navigateToSubmitModelPage();
      return;
    }

    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.showLoginModal = true;
        this.cdr.markForCheck();
        return;
      }
    } catch {
      this.showLoginModal = true;
      this.cdr.markForCheck();
      return;
    }

    this.selectedDataset = null;
    this.selectedDatasetLabelOverride = null;
    this.onboardStatusMessage = '';
    this.onboardTeeVerified = false;
    this.onboardAttestationAccepted = false;
    this.onboardTeeSession = null;
    this.cdr.markForCheck();
  }

  onModelFileSelected(event: Event): void {
    void this.handleModelFileSelected(event);
  }

  onModelWeightsFileSelected(event: Event): void {
    void this.handleModelWeightsFileSelected(event);
  }

  onPythonFileSelected(event: Event): void {
    void this.handlePythonFileSelected(event);
  }

  clearModelFile(inputId: string): void {
    this.modelProgressToken += 1;
    this.modelFile = null;
    this.isModelFileBuffering = false;
    this.modelUploadProgress = null;
    this.resetFileInput(inputId);
    this.cdr.markForCheck();
  }

  clearModelWeightsFile(inputId: string): void {
    this.modelWeightsProgressToken += 1;
    this.modelWeightsFile = null;
    this.isModelWeightsFileBuffering = false;
    this.modelWeightsUploadProgress = null;
    this.resetFileInput(inputId);
    this.cdr.markForCheck();
  }

  clearPythonFile(inputId: string): void {
    this.pythonProgressToken += 1;
    this.pythonFile = null;
    this.isPythonFileBuffering = false;
    this.pythonUploadProgress = null;
    this.resetFileInput(inputId);
    this.cdr.markForCheck();
  }

  private async handleModelFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (!file) {
      this.clearModelFile(input.id);
      this.cdr.markForCheck();
      return;
    }

    if (!this.isValidOnnxModelFile(file)) {
      this.toasterService.warning('Model file must be a .onnx or .py file.');
      this.clearModelFile(input.id);
      this.cdr.markForCheck();
      return;
    }

    const token = ++this.modelProgressToken;
    this.modelFile = file;
    this.isModelFileBuffering = true;
    this.modelUploadProgress = 0;
    this.cdr.markForCheck();
    await this.animateFileProgress(token, 'model', file.size);
    if (token !== this.modelProgressToken) {
      return;
    }
    this.modelUploadProgress = 100;
    this.isModelFileBuffering = false;
    this.cdr.markForCheck();
  }

  private async handleModelWeightsFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (!file) {
      this.clearModelWeightsFile(input.id);
      this.cdr.markForCheck();
      return;
    }

    if (!this.isValidOnnxDataWeightsFile(file)) {
      this.toasterService.warning(
        'Model weights file must be a .onnx.data or .py file.',
      );
      this.clearModelWeightsFile(input.id);
      this.cdr.markForCheck();
      return;
    }

    const token = ++this.modelWeightsProgressToken;
    this.modelWeightsFile = file;
    this.isModelWeightsFileBuffering = true;
    this.modelWeightsUploadProgress = 0;
    this.cdr.markForCheck();
    await this.animateFileProgress(token, 'weights', file.size);
    if (token !== this.modelWeightsProgressToken) {
      return;
    }
    this.modelWeightsUploadProgress = 100;
    this.isModelWeightsFileBuffering = false;
    this.cdr.markForCheck();
  }

  private async handlePythonFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (!file) {
      this.clearPythonFile(input.id);
      this.cdr.markForCheck();
      return;
    }

    const token = ++this.pythonProgressToken;
    this.pythonFile = file;
    this.isPythonFileBuffering = true;
    this.pythonUploadProgress = 0;
    this.cdr.markForCheck();
    await this.animateFileProgress(token, 'python', file.size);
    if (token !== this.pythonProgressToken) {
      return;
    }
    this.pythonUploadProgress = 100;
    this.isPythonFileBuffering = false;
    this.cdr.markForCheck();
  }

  private animateFileProgress(
    token: number,
    field: 'model' | 'weights' | 'python',
    fileSize: number,
  ): Promise<void> {
    const durationMs = Math.min(
      1400,
      Math.max(400, Math.round((fileSize / (2 * 1024 * 1024)) * 450)),
    );
    return new Promise((resolve) => {
      const start = performance.now();
      const tick = (now: number) => {
        if (!this.isFileProgressTokenCurrent(token, field)) {
          resolve();
          return;
        }
        const pct = Math.min(
          100,
          Math.round(((now - start) / durationMs) * 100),
        );
        this.setFileUploadProgress(field, pct);
        this.cdr.markForCheck();
        if (pct >= 100) {
          resolve();
          return;
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
  }

  private isFileProgressTokenCurrent(
    token: number,
    field: 'model' | 'weights' | 'python',
  ): boolean {
    if (field === 'model') {
      return token === this.modelProgressToken;
    }
    if (field === 'weights') {
      return token === this.modelWeightsProgressToken;
    }
    return token === this.pythonProgressToken;
  }

  private setFileUploadProgress(
    field: 'model' | 'weights' | 'python',
    value: number | null,
  ): void {
    if (field === 'model') {
      this.modelUploadProgress = value;
      return;
    }
    if (field === 'weights') {
      this.modelWeightsUploadProgress = value;
      return;
    }
    this.pythonUploadProgress = value;
  }

  private parseUploadPercent(detail: string): number | null {
    const match = /^(\d{1,3})%$/.exec(detail.trim());
    if (!match) {
      return null;
    }
    return Math.min(100, Math.max(0, Number(match[1])));
  }

  onOnboardConfirm(): void {
    void this.submitOnboardModel();
  }

  get onboardTransferFailed(): boolean {
    return (
      !this.isOnboardSubmitting &&
      this.onboardAttestationSteps.some((step) => step.status === 'error')
    );
  }

  retryOnboardTransfer(): void {
    if (this.isOnboardSubmitting || !this.onboardTeeSession) {
      return;
    }
    const failedIndex = this.onboardAttestationSteps.findIndex(
      (step) => step.status === 'error',
    );
    if (failedIndex < 0) {
      return;
    }
    const failed = this.onboardAttestationSteps[failedIndex];
    if (failed.id === 'done' && this.onboardTransferCheckpoint.jobId) {
      this.setOnboardAttestationStep('done', 'done', 'Job queued');
      this.onboardTransferComplete = true;
      this.cdr.markForCheck();
      return;
    }
    const resumeFrom = this.mapOnboardStepToTransferPhase(failed.id);
    if (!resumeFrom) {
      return;
    }
    this.onboardAttestationSteps.forEach((step, index) => {
      if (index < failedIndex) {
        return;
      }
      step.status = 'pending';
      step.detail = this.defaultOnboardTransferDetail(step.id);
    });
    this.onboardAttestationSteps[failedIndex].status = 'active';
    this.onboardStatusMessage = '';
    this.cdr.markForCheck();
    void this.submitOnboardModel({ resumeFrom, resetSteps: false });
  }

  /** Refresh the Keycloak token so each TEE request has at least 2 minutes left. */
  private async getOnboardKeycloakToken(): Promise<string> {
    const keycloakInstance = this.keycloak.getKeycloakInstance();
    if (!keycloakInstance.authenticated) {
      throw new Error('User is not authenticated.');
    }
    // Refresh unless the access token still has at least 2 minutes left.
    // A 30s window expires during hashing and the TEE then rejects /v1/submit.
    await keycloakInstance.updateToken(120);
    const token = keycloakInstance.token;
    if (!token) {
      throw new Error('Authentication token is not available.');
    }
    return token;
  }

  /** Attest the Buffer TEE and replace the in-memory HPKE session. */
  private async openOnboardTeeSession(): Promise<OpenedSession> {
    const session = await attestAndOpen({
      attestUrl: ONBOARD_MODEL_TEE_URL,
      getKeycloakToken: () => this.getOnboardKeycloakToken(),
    });
    this.onboardTeeSession = session;
    this.onboardTeeVerified = true;
    return session;
  }

  async verifyOnboardTee(): Promise<void> {
    if (
      this.isOnboardTeeVerifying ||
      this.onboardTeeVerified ||
      this.isOnboardSubmitting
    ) {
      return;
    }

    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.showLoginModal = true;
        return;
      }
    } catch {
      this.showLoginModal = true;
      return;
    }

    this.isOnboardTeeVerifying = true;
    this.onboardStatusMessage = '';
    this.cdr.markForCheck();

    try {
      await this.openOnboardTeeSession();
      this.toasterService.success(
        'Trusted Execution Environment verified successfully.',
      );
    } catch (error) {
      console.error('Error verifying TEE:', error);
      const raw =
        error instanceof Error
          ? error.message
          : 'Unable to verify Trusted Execution Environment. Please try again.';
      const message = this.displayOnboardError(raw);
      this.toasterService.error(message);
      this.onboardStatusMessage = message;
    } finally {
      this.isOnboardTeeVerifying = false;
      this.cdr.markForCheck();
    }
  }

  onOnboardAttestationAcceptedChange(accepted: boolean): void {
    this.onboardAttestationAccepted = accepted;
    this.cdr.markForCheck();

    if (!accepted || !isPlatformBrowser(this.platformId)) {
      return;
    }

    // Let Angular render the upload section before scrolling.
    setTimeout(() => {
      const el = document.getElementById('onboardUploadSection');
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }

  async submitOnboardModel(options?: {
    resumeFrom?: TransferPhase;
    resetSteps?: boolean;
  }): Promise<void> {
    if (
      !this.isOnboardFormValid ||
      this.isOnboardSubmitting ||
      !this.onboardTeeSession
    ) {
      return;
    }

    if (
      !this.modelFile ||
      !this.modelWeightsFile ||
      !this.selectedDataset ||
      (this.requiresPythonFile && !this.pythonFile)
    ) {
      return;
    }

    const datasetId = await this.resolveOnboardDatasetId(this.selectedDataset);
    if (!datasetId) {
      this.toasterService.warning(
        'Selected dataset is not configured for onboarding.',
      );
      return;
    }

    const teeUrl = ONBOARD_MODEL_TEE_URL;
    const session = this.onboardTeeSession;

    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.showLoginModal = true;
        return;
      }
    } catch {
      this.showLoginModal = true;
      return;
    }

    this.isOnboardSubmitting = true;
    this.onboardStatusMessage = '';
    this.modelUploadProgress = 0;
    this.modelWeightsUploadProgress = 0;
    this.pythonUploadProgress = this.pythonFile ? 0 : null;
    if (options?.resetSteps !== false) {
      this.initOnboardTransferSteps();
    }

    try {
      const { jobId, elapsedMs } = await submitOnnxModel({
        teeUrl,
        session,
        modelFile: this.modelFile,
        weightsFile: this.modelWeightsFile,
        pythonFile: this.requiresPythonFile
          ? (this.pythonFile ?? undefined)
          : undefined,
        datasetId,
        checkpoint: this.onboardTransferCheckpoint,
        renewSession: () => this.openOnboardTeeSession(),
        ...(options?.resumeFrom !== undefined
          ? { resumeFrom: options.resumeFrom }
          : {}),
        onStep: (step, detail) => this.handleOnboardUploadStep(step, detail),
      });

      const result: TransferResult = {
        oidc: this.onboardTeeSession?.oidc ?? session.oidc,
        response: { job_id: jobId, status: 'queued' },
        elapsedMs,
      };

      this.onboardTransferResult = result;
      this.onboardTransferComplete = true;
      this.toasterService.success('Model submitted successfully.');
    } catch (error) {
      console.error('Error onboarding model:', error);
      const raw =
        error instanceof Error
          ? error.message
          : 'Unable to onboard model. Please try again.';
      this.markOnboardAttestationError(raw);
      const message =
        this.onboardAttestationSteps.find((step) => step.status === 'error')
          ?.detail ?? this.displayOnboardError(raw);
      this.toasterService.error(message);
      this.onboardStatusMessage = message;
    } finally {
      this.isOnboardSubmitting = false;
      this.cdr.markForCheck();
    }
  }

  closeOnboardModalAfterSuccess(): void {
    this.cancelOnboardResultsFetch();
    this.resetOnboardForm();
    this.leaveSubmitModelFlow();
  }

  viewOnboardSubmission(): void {
    this.cancelOnboardResultsFetch();
    const ds = this.findHackathonDatasetByKey(this.selectedDataset || '');
    const datasetId = ds?.leaderboardDatasetId || ds?.id;
    void this.router.navigate(['/my-submissions'], {
      queryParams: datasetId ? { dataset: datasetId } : undefined,
    });
  }

  checkOnboardEvaluationResults(): void {
    void this.fetchOnboardJobResults({ showFetching: true });
  }

  private cancelOnboardResultsFetch(): void {
    this.onboardResultsFetchAbort = true;
  }

  private startOnboardResultsPolling(): void {
    this.onboardResultsFetchAbort = false;
    void this.pollOnboardJobResults();
  }

  private async pollOnboardJobResults(): Promise<void> {
    for (
      let attempt = 0;
      attempt < ReferenceDatasetsComponent.ONBOARD_RESULTS_POLL_MAX_ATTEMPTS;
      attempt++
    ) {
      if (this.onboardResultsFetchAbort || this.isOnboardInferenceComplete()) {
        return;
      }

      await this.fetchOnboardJobResults({ showFetching: attempt === 0 });

      if (this.onboardResultsFetchAbort || this.isOnboardInferenceComplete()) {
        return;
      }
      if (this.onboardJobEvaluationStatus === 'error') {
        return;
      }

      await new Promise<void>((resolve) => {
        setTimeout(
          resolve,
          ReferenceDatasetsComponent.ONBOARD_RESULTS_POLL_INTERVAL_MS,
        );
      });
    }
  }

  private isOnboardInferenceComplete(): boolean {
    return this.onboardJobEvaluationStatus === 'complete';
  }

  private async fetchOnboardJobResults(
    options: { showFetching?: boolean } = {},
  ): Promise<void> {
    const session = this.onboardTeeSession;
    const jobId = this.onboardJobId;
    if (!session || !jobId || this.isOnboardInferenceComplete()) {
      return;
    }

    if (options.showFetching) {
      this.onboardJobEvaluationStatus = 'fetching';
      this.onboardStatusMessage = '';
      this.cdr.markForCheck();
    } else if (this.onboardJobEvaluationStatus === 'idle') {
      this.onboardJobEvaluationStatus = 'pending';
    }

    try {
      const result = await fetchJobResults(session, jobId);

      if (this.onboardResultsFetchAbort) {
        return;
      }

      if (result) {
        this.onboardJobEvaluationResult = result;
        this.onboardJobEvaluationStatus = 'complete';
        this.toasterService.success(
          `Model inference complete. Accuracy: ${this.formatOnboardAccuracy(result.accuracy)}`,
        );
        return;
      }

      this.onboardJobEvaluationStatus = 'pending';
    } catch (error) {
      if (this.onboardResultsFetchAbort) {
        return;
      }
      console.error('Error fetching evaluation results:', error);
      this.onboardJobEvaluationStatus = 'error';
      this.onboardStatusMessage =
        error instanceof Error
          ? error.message
          : 'Unable to fetch evaluation results. Please try again later.';
    } finally {
      this.cdr.markForCheck();
    }
  }

  onOnboardModalClose(): void {
    this.leaveSubmitModelFlow();
  }

  leaveSubmitModelFlow(): void {
    if (this.isOnboardSubmitting || this.isOnboardTeeVerifying) {
      return;
    }
    this.onOnboardCancel();
    this.onboardHostClosed.emit();
    if (isPlatformBrowser(this.platformId) && window.history.length > 1) {
      this.location.back();
      return;
    }
    void this.router.navigate(['/datasets']);
  }

  private findHackathonDatasetByKey(key: string): HackathonDataset | undefined {
    const trimmed = key.trim();
    if (!trimmed) {
      return undefined;
    }

    return (
      HACKATHON_DATASETS.find(
        (dataset) =>
          dataset.id === trimmed || dataset.leaderboardDatasetId === trimmed,
      ) ??
      findHackathonDatasetBySlug(trimmed) ??
      findHackathonDatasetForCatalogueItem({ id: trimmed, title: trimmed })
    );
  }

  private async tryAutoOpenOnboardModal(): Promise<void> {
    const key = this.autoOpenDatasetKey?.trim();
    const label = this.autoOpenDatasetLabel?.trim();

    if (key) {
      const dataset = this.findHackathonDatasetByKey(key);
      if (dataset) {
        await this.openOnboardModal(dataset, undefined, label);
        return;
      }
      await this.openOnboardModalWithDatasetName(label || key, key);
      return;
    }

    if (label) {
      await this.openOnboardModalWithDatasetName(label, key);
      return;
    }

    await this.openOnboardModalWithoutDataset();
  }

  private navigateToSubmitModelPage(
    ds?: HackathonDataset,
    datasetLabel?: string,
    fallbackDatasetKey?: string,
  ): void {
    void this.router.navigate([SUBMIT_MODEL_ROUTE], {
      queryParams: submitModelQueryParams({
        datasetKey: ds?.leaderboardDatasetId || ds?.id || fallbackDatasetKey,
        label: datasetLabel || ds?.title,
      }),
    });
  }

  private redirectLegacyOnboardState(openOnboardId: string): void {
    const ds = this.findHackathonDatasetByKey(openOnboardId);
    void this.router.navigate([SUBMIT_MODEL_ROUTE], {
      queryParams: submitModelQueryParams({
        datasetKey: ds?.leaderboardDatasetId || openOnboardId,
        label: ds?.title,
      }),
    });
  }

  private initOnboardTransferSteps(): void {
    this.wizardStep = 4;
    this.showOnboardTransferSection = true;
    this.onboardTransferComplete = false;
    this.onboardTransferResult = null;
    this.onboardJobEvaluationStatus = 'idle';
    this.onboardJobEvaluationResult = null;
    this.cancelOnboardResultsFetch();
    this.onboardTransferCheckpoint = {};
    this.onboardAttestationSteps = [
      {
        id: 'hash',
        title: 'Hash model & weights',
        detail: 'Streaming SHA-256',
        status: 'pending',
      },
      {
        id: 'submit',
        title: 'Encrypt payload & submit',
        detail: 'HPKE X25519 / AES-256-GCM · POST /v1/submit',
        status: 'pending',
      },
      {
        id: 'upload_model',
        title: 'Upload model',
        detail: 'AES-256-GCM-CHUNKED-v1',
        status: 'pending',
      },
      {
        id: 'upload_weights',
        title: 'Upload weights',
        detail: 'AES-256-GCM-CHUNKED-v1',
        status: 'pending',
      },
      ...(this.pythonFile
        ? [
            {
              id: 'upload_python',
              title: 'Upload python script',
              detail: 'AES-256-GCM-CHUNKED-v1',
              status: 'pending' as OnboardAttestationStepStatus,
            },
          ]
        : []),
      {
        id: 'done',
        title: 'Job queued',
        detail: 'Pending',
        status: 'pending',
      },
    ];
  }

  /** Text after the library's " — ", unwrapping a JSON error body when present. */
  private serverErrorText(message: string): string | null {
    const marker = ' — ';
    const idx = message.lastIndexOf(marker);
    if (idx < 0) {
      return null;
    }
    let body = message.slice(idx + marker.length).trim();
    if (!body) {
      return null;
    }
    if (body.startsWith('{') || body.startsWith('[')) {
      try {
        const parsed = JSON.parse(body) as unknown;
        const text = this.errorBodyField(parsed);
        if (text) {
          body = text;
        }
      } catch {
        // The TEE returned plain text, not JSON.
      }
    }
    return body;
  }

  private errorBodyField(value: unknown): string | null {
    if (!value || typeof value !== 'object') {
      return null;
    }
    const record = value as Record<string, unknown>;
    for (const key of ['error_message', 'message', 'error', 'detail']) {
      const field = record[key];
      if (typeof field === 'string' && field.trim()) {
        return field.trim();
      }
    }
    return null;
  }

  /** The TEE or library error text, not a fixed sentence per step. */
  private displayOnboardError(message: string): string {
    return this.serverErrorText(message) ?? message;
  }

  private markOnboardAttestationError(message: string): void {
    if (this.onboardAttestationSteps.some((step) => step.status === 'error')) {
      return;
    }
    const target =
      this.onboardAttestationSteps.find((step) => step.status === 'active') ??
      this.onboardAttestationSteps.find((step) => step.status === 'pending');
    if (!target) {
      return;
    }
    target.status = 'error';
    target.detail = this.displayOnboardError(message);
  }

  private mapOnboardStepToTransferPhase(
    stepId: string,
  ): TransferPhase | undefined {
    switch (stepId) {
      case 'hash':
        return 'model_hash';
      case 'submit':
        return 'submit_meta';
      case 'upload_model':
        return 'upload_model';
      case 'upload_weights':
        return 'upload_weights';
      case 'upload_python':
        return 'upload_python';
      default:
        return undefined;
    }
  }

  private defaultOnboardTransferDetail(stepId: string): string {
    switch (stepId) {
      case 'hash':
        return 'Streaming SHA-256';
      case 'submit':
        return 'HPKE X25519 / AES-256-GCM';
      case 'upload_model':
      case 'upload_weights':
      case 'upload_python':
        return 'AES-256-GCM-CHUNKED-v1';
      case 'done':
        return 'Pending';
      default:
        return '';
    }
  }

  private setOnboardAttestationStep(
    id: string,
    status: OnboardAttestationStepStatus,
    detail?: string,
  ): void {
    const step = this.onboardAttestationSteps.find((item) => item.id === id);
    if (!step) {
      return;
    }
    step.status = status;
    if (detail !== undefined) {
      step.detail = detail;
    }
  }

  /**
   * The catalogue UUID to submit for evaluation. `datasetKey` is normally
   * already the real catalogue id (carried through from wherever the user
   * picked the dataset). Only the legacy manual picker still produces the
   * bare "Breast Cancer" / "Oral Cancer" labels with no id attached — for
   * those, look the dataset up in the catalogue to get its real UUID.
   */
  private async resolveOnboardDatasetId(
    datasetKey: string,
  ): Promise<string | null> {
    const key = datasetKey?.trim() ?? '';
    if (!key) {
      return null;
    }
    if (key !== 'Breast Cancer' && key !== 'Oral Cancer') {
      return key;
    }
    return this.lookupOnboardDatasetUuidFromCatalogue(key);
  }

  /**
   * Resolves a dataset name to its catalogue UUID via the controlplane
   * catalogue API, caching the result for the lifetime of the component.
   */
  private async lookupOnboardDatasetUuidFromCatalogue(
    name: string,
  ): Promise<string | null> {
    const cached = this.onboardDatasetUuidCache.get(name);
    if (cached) {
      return cached;
    }

    try {
      const response = await this.catalogueApiService.searchAllAssets(
        name,
        ['Dataset'],
        ['id', 'name'],
        1,
        1,
      );
      const items = this.extractItemsFromResponse(response);
      const first = items.find(
        (item): item is Record<string, unknown> =>
          !!item && typeof item === 'object' && !Array.isArray(item),
      );
      const id = first ? String(first['id'] ?? '').trim() : '';
      if (!id) {
        return null;
      }
      this.onboardDatasetUuidCache.set(name, id);
      return id;
    } catch (error) {
      console.error(`Failed to resolve catalogue UUID for "${name}":`, error);
      return null;
    }
  }

  /**
   * Normalize catalogue/static ids and labels to the TEE onboard keys
   * (`Breast Cancer` / `Oral Cancer`) when no real catalogue id is known.
   */
  private resolveCanonicalOnboardDatasetKey(
    datasetKey?: string | null,
    label?: string | null,
  ): string | null {
    const key = datasetKey?.trim() ?? '';
    const name = label?.trim() ?? '';

    if (key) {
      // A real id (catalogue UUID or static-dataset id) always wins — never
      // replace it with a text-based "Breast Cancer" / "Oral Cancer" guess.
      const matched = this.findHackathonDatasetByKey(key);
      return matched?.id ?? key;
    }

    const fromLabel = this.mapTextToOnboardDatasetId(name);
    if (fromLabel === 1) {
      return 'Breast Cancer';
    }
    if (fromLabel === 2) {
      return 'Oral Cancer';
    }

    return name || null;
  }

  private mapTextToOnboardDatasetId(value?: string | null): 1 | 2 | null {
    const normalized = (value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ');
    if (!normalized) {
      return null;
    }

    if (
      normalized === 'breast cancer' ||
      normalized === 'breastcancer' ||
      normalized.includes('breast')
    ) {
      return 1;
    }

    if (
      normalized === 'oral cancer' ||
      normalized === 'oralcancer' ||
      normalized.includes('oral')
    ) {
      return 2;
    }

    return null;
  }

  private handleOnboardUploadStep(step: UploadStepName, detail: string): void {
    const percent = this.parseUploadPercent(detail);
    switch (step) {
      case 'model_hash':
        if (detail === 'SHA-256 computed') {
          this.setOnboardAttestationStep('hash', 'done', 'SHA-256 computed ✓');
        } else {
          this.setOnboardAttestationStep('hash', 'active', detail);
        }
        break;
      case 'submit_meta':
        this.setOnboardAttestationStep('hash', 'done', 'SHA-256 computed ✓');
        this.setOnboardAttestationStep('submit', 'active', 'HPKE sealing…');
        break;
      case 'upload_model':
        this.setOnboardAttestationStep('submit', 'done', 'HPKE sealed ✓');
        this.modelUploadProgress = percent ?? this.modelUploadProgress ?? 0;
        if (detail.endsWith('%')) {
          this.setOnboardAttestationStep(
            'upload_model',
            'active',
            `${detail} uploaded`,
          );
        } else {
          this.setOnboardAttestationStep('upload_model', 'active', detail);
        }
        break;
      case 'upload_weights':
        this.setOnboardAttestationStep(
          'upload_model',
          'done',
          'Model uploaded ✓',
        );
        this.modelUploadProgress = 100;
        this.modelWeightsUploadProgress =
          percent ?? this.modelWeightsUploadProgress ?? 0;
        if (detail.endsWith('%')) {
          this.setOnboardAttestationStep(
            'upload_weights',
            'active',
            `${detail} uploaded`,
          );
        } else {
          this.setOnboardAttestationStep('upload_weights', 'active', detail);
        }
        break;
      case 'upload_python':
        this.setOnboardAttestationStep(
          'upload_weights',
          'done',
          'Weights uploaded ✓',
        );
        this.modelWeightsUploadProgress = 100;
        this.pythonUploadProgress = percent ?? this.pythonUploadProgress ?? 0;
        if (detail.endsWith('%')) {
          this.setOnboardAttestationStep(
            'upload_python',
            'active',
            `${detail} uploaded`,
          );
        } else {
          this.setOnboardAttestationStep('upload_python', 'active', detail);
        }
        break;
      case 'done':
        this.modelUploadProgress = 100;
        this.modelWeightsUploadProgress = 100;
        if (this.pythonFile) {
          this.pythonUploadProgress = 100;
          this.setOnboardAttestationStep(
            'upload_python',
            'done',
            'Python script uploaded ✓',
          );
        } else {
          this.setOnboardAttestationStep(
            'upload_weights',
            'done',
            'Weights uploaded ✓',
          );
        }
        this.setOnboardAttestationStep('done', 'done', detail);
        break;
      case 'error':
        this.markOnboardAttestationError(detail);
        this.onboardStatusMessage =
          this.onboardAttestationSteps.find((step) => step.status === 'error')
            ?.detail ?? this.displayOnboardError(detail);
        break;
    }
    this.cdr.markForCheck();
  }

  onOnboardCancel(): void {
    if (this.isOnboardSubmitting || this.isOnboardTeeVerifying) {
      return;
    }
    this.resetOnboardForm();
  }

  private resetOnboardForm(): void {
    this.cancelOnboardResultsFetch();
    this.wizardStep = 1;
    this.selectedDataset = null;
    this.selectedDatasetLabelOverride = null;
    this.modelProgressToken += 1;
    this.modelWeightsProgressToken += 1;
    this.pythonProgressToken += 1;
    this.modelFile = null;
    this.modelWeightsFile = null;
    this.pythonFile = null;
    this.isModelFileBuffering = false;
    this.isModelWeightsFileBuffering = false;
    this.isPythonFileBuffering = false;
    this.modelUploadProgress = null;
    this.modelWeightsUploadProgress = null;
    this.pythonUploadProgress = null;
    this.isOnboardTeeVerifying = false;
    this.onboardTeeVerified = false;
    this.onboardAttestationAccepted = false;
    this.onboardStatusMessage = '';
    this.showOnboardTransferSection = false;
    this.onboardTransferComplete = false;
    this.onboardTransferResult = null;
    this.onboardAttestationSteps = [];
    this.onboardTransferCheckpoint = {};
    this.onboardJobEvaluationStatus = 'idle';
    this.onboardJobEvaluationResult = null;
    this.onboardTeeSession = null;
    this.resetFileInput('onboardModelFile');
    this.resetFileInput('onboardModelWeightsFile');
    this.resetFileInput('onboardPythonFile');
  }

  private isValidOnnxModelFile(file: File): boolean {
    const name = file.name.toLowerCase();
    return name.endsWith('.onnx') || name.endsWith('.py');
  }

  private isValidOnnxDataWeightsFile(file: File): boolean {
    const name = file.name.toLowerCase();
    return name.endsWith('.onnx.data') || name.endsWith('.py');
  }

  private resetFileInput(inputId: string): void {
    const input = document.getElementById(inputId) as HTMLInputElement | null;
    if (input) {
      input.value = '';
    }
  }

  /**
   * POST sandbox leaderboard for this reference dataset (challenge-evaluator-backend).
   */
  async onEvaluateSandbox(ds: HackathonDataset, event?: Event): Promise<void> {
    event?.stopPropagation();
    if (this.isSandboxEvaluateSubmitting) {
      return;
    }

    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.showLoginModal = true;
        return;
      }

      const pvc = ds.evaluatorSandboxPvc?.trim();
      if (!pvc) {
        this.toasterService.warning(
          'Sandbox evaluation is not configured for this dataset.',
        );
        return;
      }

      this.isSandboxEvaluateSubmitting = true;
      this.sandboxEvaluatingDatasetId = ds.id;

      const accessToken = await this.keycloak.getToken();
      const response = await fetch(REFERENCE_DATASET_SANDBOX_LEADERBOARD_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ pvc }),
      });

      const responseText = await response.text();
      if (!response.ok) {
        let detail = responseText || `Request failed (${response.status}).`;
        try {
          const parsed = JSON.parse(responseText) as {
            detail?: unknown;
            message?: unknown;
            error?: unknown;
          };
          const candidate = parsed.detail ?? parsed.message ?? parsed.error;
          if (typeof candidate === 'string' && candidate.trim()) {
            detail = candidate.trim();
          }
        } catch {
          // keep responseText as detail
        }
        this.toasterService.error(detail);
        return;
      }

      this.sandboxEvaluateSuccessContext = {
        datasetTitle: ds.title,
        pvc,
      };
      this.onSandboxEvaluateSuccessVisibleSync(true);
    } catch (error) {
      console.error(
        'Error calling sandbox leaderboard for reference dataset:',
        error,
      );
      this.toasterService.error(
        'Unable to evaluate sandbox. Please try again.',
      );
    } finally {
      this.isSandboxEvaluateSubmitting = false;
      this.sandboxEvaluatingDatasetId = null;
    }
  }

  getEvaluateSandboxButtonLabel(datasetId: string): string {
    if (
      this.isSandboxEvaluateSubmitting &&
      this.sandboxEvaluatingDatasetId === datasetId
    ) {
      return 'Submitting…';
    }
    return 'Evaluate sandbox';
  }

  closeSandboxEvaluateSuccessModal(): void {
    this.onSandboxEvaluateSuccessVisibleSync(false);
  }

  onSandboxEvaluateSuccessVisibleSync(visible: boolean): void {
    this.showSandboxEvaluateSuccessModal = visible;
    if (!visible) {
      this.sandboxEvaluateSuccessContext = null;
    }
  }

  async openSandbox(ps: ProblemStatement, event?: Event): Promise<void> {
    event?.stopPropagation();
    if (this.isSandboxRedirecting) {
      return;
    }

    this.isSandboxRedirecting = true;
    this.redirectingSandboxProblemId = ps.id;

    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.showLoginModal = true;
        this.resetSandboxRedirectState();
        return;
      }

      await this.rolesService.getRoles();
      const userRoles = this.rolesService.getUserRoles();
      const panelRoute = this.rolesService.getHighestRolePanelRoute(
        userRoles || [],
      );

      if (panelRoute.startsWith('/admin')) {
        this.toasterService.warning(
          'You dont have access to create the sandbox',
        );
        this.resetSandboxRedirectState();
        return;
      }

      const myProjectsRoute = this.getMyProjectsRouteFromPanel(panelRoute);
      const hasExistingNotebooks = await this.hasExistingNotebooks();
      const tab = hasExistingNotebooks ? 'list' : 'create';

      const navigated = await this.router.navigate([myProjectsRoute], {
        queryParams: {
          tab,
          source: 'hackathon',
          psId: ps.id,
          psName: ps.title,
        },
      });
      if (!navigated) {
        this.resetSandboxRedirectState();
      }
    } catch (error) {
      console.error('Error opening sandbox from hackathon:', error);
      this.toasterService.warning('Unable to open Sandbox. Please try again.');
      this.resetSandboxRedirectState();
    }
  }

  async evaluateProblemStatement(
    ps: ProblemStatement,
    event?: Event,
  ): Promise<void> {
    event?.stopPropagation();
    if (this.isEvaluateSubmitting) {
      return;
    }
    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.showLoginModal = true;
        return;
      }
      if (this.isEvaluateDisabledForProblemStatement(ps.id)) {
        this.toasterService.warning(
          'Create or open a sandbox notebook for this problem statement before evaluating.',
        );
        return;
      }

      const payload = this.getEvaluationPayloadForProblemStatement(ps.id);
      if (!payload) {
        this.toasterService.warning(
          'Evaluation is not configured for this problem statement.',
        );
        return;
      }

      const canContinue = await this.ensureNotebookNotRunningForEvaluate(ps);
      if (!canContinue) {
        return;
      }

      this.isEvaluateSubmitting = true;
      this.evaluatingProblemId = ps.id;
      const accessToken = await this.keycloak.getToken();
      const submitResponse: HackathonSubmitResponse =
        await this.leadershipApiService.submitHackathonEvaluation(
          payload.challengeName,
          payload.pvc,
          accessToken,
        );
      if (submitResponse?._httpStatus === 202) {
        this.showEvaluateModal = true;
      } else {
        this.toasterService.success('Evaluation submitted successfully.');
      }
    } catch (error) {
      console.error('Error submitting hackathon evaluation:', error);
      const typedError = error as HackathonSubmitError;
      this.toasterService.error(
        this.getApiErrorMessage(
          typedError,
          'Unable to submit evaluation. Please try again.',
        ),
      );
    } finally {
      this.isEvaluateSubmitting = false;
      this.evaluatingProblemId = null;
    }
  }

  async viewMyEvaluation(ps: ProblemStatement, event?: Event): Promise<void> {
    event?.stopPropagation();
    if (this.isViewEvaluationLoading) {
      return;
    }

    const isLoggedIn = await this.keycloak.isLoggedIn();
    if (!isLoggedIn) {
      this.showLoginModal = true;
      return;
    }

    const payload = this.getEvaluationPayloadForProblemStatement(ps.id);
    if (!payload) {
      this.toasterService.warning(
        'Evaluation is not configured for this problem statement.',
      );
      return;
    }

    this.isViewEvaluationLoading = true;
    this.viewingEvaluationProblemId = ps.id;

    try {
      const accessToken = await this.keycloak.getToken();
      const workflowsResponse =
        await this.leadershipApiService.getHackathonMyWorkflows(
          payload.pvc,
          accessToken,
        );
      const workflows =
        extractHackathonMyWorkflowsListFromResponse(workflowsResponse);
      this.myWorkflowRows = workflows;
      if (workflows.length > 0) {
        this.showMyEvaluationsModal = true;
      } else {
        this.showNoEvaluationModal = true;
      }
    } catch (error) {
      console.error('Error fetching my evaluation workflows:', error);
      const typedError = error as HackathonSubmitError;
      this.toasterService.error(
        this.getApiErrorMessage(
          typedError,
          'Unable to fetch your evaluations. Please try again.',
        ),
      );
    } finally {
      this.isViewEvaluationLoading = false;
      this.viewingEvaluationProblemId = null;
    }
  }

  formatWorkflowScore(value: number | undefined): string {
    if (value === undefined || value === null || Number.isNaN(Number(value))) {
      return '—';
    }
    return (Math.round(Number(value) * 10000) / 10000).toFixed(4);
  }

  formatWorkflowDate(iso: string | undefined): string {
    if (!iso) {
      return '—';
    }
    const parsed = Date.parse(iso);
    if (Number.isNaN(parsed)) {
      return iso;
    }
    return new Date(parsed).toLocaleString();
  }

  workflowStatusClass(status: string | undefined): string {
    const key = (status || '').toLowerCase();
    if (key === 'succeeded') {
      return 'hack-my-eval__status hack-my-eval__status--ok';
    }
    if (key === 'failed') {
      return 'hack-my-eval__status hack-my-eval__status--fail';
    }
    if (key === 'running') {
      return 'hack-my-eval__status hack-my-eval__status--run';
    }
    return 'hack-my-eval__status';
  }

  async toggleWorkflowLog(row: HackathonMyWorkflowRow): Promise<void> {
    const runId = row.id || '';
    if (!runId) {
      return;
    }

    if (this.expandedWorkflowId === runId) {
      this.expandedWorkflowId = null;
      return;
    }

    this.expandedWorkflowId = runId;
    if (this.workflowLogsById[runId] || this.workflowLogsLoadingById[runId]) {
      return;
    }

    this.workflowLogsLoadingById[runId] = true;
    try {
      const accessToken = await this.keycloak.getToken();
      const runResponse: HackathonRunDetailsResponse =
        await this.leadershipApiService.getHackathonRunDetails(
          runId,
          accessToken,
        );
      this.workflowLogsById[runId] = this.normalizeRunOutput(
        runResponse['output'],
      );
    } catch (error) {
      console.error('Error fetching workflow logs:', error);
      const typedError = error as HackathonSubmitError;
      this.workflowLogsById[runId] = this.getApiErrorMessage(
        typedError,
        'Unable to fetch logs.',
      );
    } finally {
      this.workflowLogsLoadingById[runId] = false;
    }
  }

  getWorkflowToggleLabel(row: HackathonMyWorkflowRow): string {
    const runId = row.id || '';
    if (!runId) {
      return 'No logs';
    }
    if (this.workflowLogsLoadingById[runId]) {
      return 'Loading logs...';
    }
    return this.expandedWorkflowId === runId ? 'Hide logs' : 'View logs';
  }

  getWorkflowLogText(row: HackathonMyWorkflowRow): string {
    const runId = row.id || '';
    if (!runId) {
      return 'Run id is unavailable for this row.';
    }
    if (this.workflowLogsLoadingById[runId]) {
      return 'Loading logs...';
    }
    return this.workflowLogsById[runId] || 'No output available.';
  }

  isWorkflowLogLoading(row: HackathonMyWorkflowRow): boolean {
    const runId = row.id || '';
    return !!runId && !!this.workflowLogsLoadingById[runId];
  }

  getViewEvaluationButtonLabel(psId: number): string {
    if (
      this.isViewEvaluationLoading &&
      this.viewingEvaluationProblemId === psId
    ) {
      return 'Fetching...';
    }
    return 'View my evaluation';
  }

  async goToMyProjectsNotebookListFromRunningModal(): Promise<void> {
    if (this.isNotebookListNavigating || !this.pendingEvaluationProblem) {
      return;
    }

    const problem = this.pendingEvaluationProblem;
    this.isNotebookListNavigating = true;
    try {
      await this.rolesService.getRoles();
      const userRoles = this.rolesService.getUserRoles();
      const panelRoute = this.rolesService.getHighestRolePanelRoute(
        userRoles || [],
      );

      if (panelRoute.startsWith('/admin')) {
        this.toasterService.warning(
          'You dont have access to create the sandbox',
        );
        return;
      }

      const myProjectsRoute = this.getMyProjectsRouteFromPanel(panelRoute);
      this.resetNotebookRunningGateState();
      await this.router.navigate([myProjectsRoute], {
        queryParams: {
          tab: 'list',
          source: 'hackathon',
          action: 'evaluate',
          psId: problem.id,
          psName: problem.title,
        },
      });
    } catch (error) {
      console.error('Error navigating to notebook list from hackathon:', error);
      this.toasterService.warning(
        'Unable to open My Projects. Please try again.',
      );
    } finally {
      this.isNotebookListNavigating = false;
    }
  }

  closeNotebookRunningModal(): void {
    if (this.isNotebookListNavigating) {
      return;
    }
    this.resetNotebookRunningGateState();
  }

  isEvaluationCooldownActive(psId: number): boolean {
    const nextAllowedAtMs = this.getEvaluateCooldownUntilMs(psId);
    if (!nextAllowedAtMs) {
      return false;
    }
    if (Date.now() >= nextAllowedAtMs) {
      this.clearEvaluateCooldown(psId);
      return false;
    }
    return true;
  }

  isEvaluateDisabledForProblemStatement(psId: number): boolean {
    return this.psNotebookEligibility[psId] === false;
  }

  getEvaluateButtonLabel(psId: number): string {
    if (this.isEvaluateSubmitting && this.evaluatingProblemId === psId) {
      return 'Submitting...';
    }
    return 'Evaluate';
  }

  private getEvaluationPayloadForProblemStatement(
    psId: number,
  ): { challengeName: string; pvc: string } | null {
    const payloadMap: Record<number, { challengeName: string; pvc: string }> = {
      1: {
        challengeName: 'Clinical Document Classification',
        pvc: 'clinical-document-classification-complia-pvc',
      },
      2: {
        challengeName:
          'Radiological Image-Based Condition Detection & Report Correlation',
        pvc: 'radiological-image-based-condition-detec-pvc',
      },
      3: {
        challengeName: 'Document Forgery / Deepfake Detection',
        pvc: 'document-forgery-deepfake-detection-pvc',
      },
    };

    return payloadMap[psId] ?? null;
  }

  private persistEvaluateCooldown(psId: number, cooldownUntil: Date): void {
    localStorage.setItem(
      this.getEvaluateCooldownStorageKey(psId),
      cooldownUntil.toISOString(),
    );
  }

  private getEvaluateCooldownUntilMs(psId: number): number | null {
    const rawValue = localStorage.getItem(
      this.getEvaluateCooldownStorageKey(psId),
    );
    if (!rawValue) {
      return null;
    }
    const parsedMs = Date.parse(rawValue);
    if (Number.isNaN(parsedMs)) {
      this.clearEvaluateCooldown(psId);
      return null;
    }
    return parsedMs;
  }

  private clearEvaluateCooldown(psId: number): void {
    localStorage.removeItem(this.getEvaluateCooldownStorageKey(psId));
  }

  private getEvaluateCooldownStorageKey(psId: number): string {
    return `${this.evaluateCooldownStoragePrefix}${this.getCurrentUserCooldownScope()}:${psId}`;
  }

  private getCurrentUserCooldownScope(): string {
    const keycloakInstance = (this.keycloak as any)?.getKeycloakInstance?.();
    const tokenParsed = keycloakInstance?.tokenParsed as
      | { sub?: string; preferred_username?: string; email?: string }
      | undefined;
    return (
      tokenParsed?.sub ||
      tokenParsed?.preferred_username ||
      tokenParsed?.email ||
      'anonymous'
    );
  }

  private getCooldownCountdownLabel(psId: number): string | null {
    const nextAllowedAtMs = this.getEvaluateCooldownUntilMs(psId);
    if (!nextAllowedAtMs) {
      return null;
    }
    const remainingMs = Math.max(0, nextAllowedAtMs - this.nowMs);
    const totalSeconds = Math.ceil(remainingMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  private startCooldownTicker(): void {
    if (this.cooldownTicker) {
      return;
    }
    this.cooldownTicker = setInterval(() => {
      this.nowMs = Date.now();
    }, 1000);
  }

  private stopCooldownTicker(): void {
    if (!this.cooldownTicker) {
      return;
    }
    clearInterval(this.cooldownTicker);
    this.cooldownTicker = null;
  }

  private resolveCooldownTimestamp(error: HackathonSubmitError): Date | null {
    if (error.nextAllowedAt) {
      const parsedMs = Date.parse(error.nextAllowedAt);
      if (!Number.isNaN(parsedMs)) {
        return new Date(parsedMs);
      }
    }

    const detail = error?.detail || error?.message || '';
    const fallbackMatch = detail.match(
      /next allowed at\s+(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z)/i,
    );
    if (!fallbackMatch?.[1]) {
      return null;
    }

    const parsedMs = Date.parse(fallbackMatch[1]);
    return Number.isNaN(parsedMs) ? null : new Date(parsedMs);
  }

  private getApiErrorMessage(
    error: HackathonSubmitError,
    fallback: string,
  ): string {
    const detail = (error?.detail || '').trim();
    if (detail) {
      const extractedDetail = this.extractReadableApiMessage(detail);
      return extractedDetail || detail;
    }
    const message = (error?.message || '').trim();
    if (!message) {
      return fallback;
    }
    const normalized = message.replace(/^Hackathon [^:]+:\s*/i, '').trim();
    const extractedMessage = this.extractReadableApiMessage(normalized);
    return extractedMessage || normalized || fallback;
  }

  private extractReadableApiMessage(raw: string): string | null {
    const trimmed = raw.trim();
    if (!trimmed) {
      return null;
    }

    // Handle cases like {"error":"maximum 5 submissions per day reached"}
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmed) as {
          error?: unknown;
          detail?: unknown;
          message?: unknown;
        };
        const candidate = parsed.error ?? parsed.detail ?? parsed.message;
        if (typeof candidate === 'string' && candidate.trim()) {
          return candidate.trim();
        }
      } catch {
        // fall back to raw text below
      }
    }

    return null;
  }

  private normalizeRunOutput(output: unknown): string {
    if (output === undefined || output === null) {
      return 'No output available.';
    }
    if (typeof output === 'string') {
      return output.trim() || 'No output available.';
    }
    try {
      return JSON.stringify(output, null, 2);
    } catch {
      return String(output);
    }
  }

  private resetSandboxRedirectState(): void {
    this.isSandboxRedirecting = false;
    this.redirectingSandboxProblemId = null;
  }

  private async ensureNotebookNotRunningForEvaluate(
    ps: ProblemStatement,
  ): Promise<boolean> {
    try {
      const response = await this.sandboxApiService.listNotebooks();
      const notebooks = this.extractNotebookItems(response);
      const runningNotebook = notebooks.find(
        (item) =>
          this.isNotebookRunning(item) &&
          this.notebookBelongsToProblemStatement(item, ps.id),
      );
      if (!runningNotebook) {
        return true;
      }

      this.pendingEvaluationProblem = ps;
      this.runningNotebookName = this.resolveNotebookName(runningNotebook);
      this.showNotebookRunningModal = true;
      return false;
    } catch (error) {
      console.error(
        'Unable to validate notebook state before evaluate:',
        error,
      );
      this.toasterService.warning(
        'Unable to verify notebook status. Please try again.',
      );
      return false;
    }
  }

  private resetNotebookRunningGateState(): void {
    this.showNotebookRunningModal = false;
    this.runningNotebookName = '';
    this.pendingEvaluationProblem = null;
  }

  private getMyProjectsRouteFromPanel(panelRoute: string): string {
    if (panelRoute.startsWith('/org-admin')) {
      return '/org-admin/my-projects';
    }

    if (panelRoute.startsWith('/provider')) {
      return '/provider/my-projects';
    }

    if (panelRoute.startsWith('/admin')) {
      return '/admin/project';
    }

    return '/consumer/my-projects';
  }

  private async hasExistingNotebooks(): Promise<boolean> {
    try {
      const response = await this.sandboxApiService.listNotebooks();
      const notebooks = this.extractNotebookItems(response);
      return notebooks.length > 0;
    } catch (error) {
      console.warn(
        'Unable to fetch notebook list. Falling back to list tab.',
        error,
      );
      // Avoid false negatives: when in doubt, prefer list tab.
      return true;
    }
  }

  private extractNotebookItems(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (response?.result) {
      if (Array.isArray(response.result)) {
        return response.result;
      }
      if (Array.isArray(response.result.results)) {
        return response.result.results;
      }
      if (Array.isArray(response.result.data)) {
        return response.result.data;
      }
      if (Array.isArray(response.result.notebooks)) {
        return response.result.notebooks;
      }
      if (Array.isArray(response.result.notebooks?.notebooks)) {
        return response.result.notebooks.notebooks;
      }
    }

    if (Array.isArray(response?.notebooks)) {
      return response.notebooks;
    }
    if (Array.isArray(response?.notebooks?.notebooks)) {
      return response.notebooks.notebooks;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (Array.isArray(response?.items)) {
      return response.items;
    }

    return [];
  }

  private async refreshEvaluateNotebookEligibility(): Promise<void> {
    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.psNotebookEligibility = {};
        return;
      }
      const response = await this.sandboxApiService.listNotebooks();
      const notebooks = this.extractNotebookItems(response);
      const next: Record<number, boolean | null> = {};
      for (const ps of this.problemStatements) {
        next[ps.id] = notebooks.some((item) =>
          this.notebookBelongsToProblemStatement(item, ps.id),
        );
      }
      this.psNotebookEligibility = next;
    } catch (error) {
      console.warn('Unable to refresh evaluate notebook eligibility:', error);
      this.psNotebookEligibility = {};
    }
  }

  /**
   * A notebook belongs to this problem statement when PVC / image / name match
   * the evaluation payload for that PS (same rules as the running-notebook gate).
   */
  private notebookBelongsToProblemStatement(
    notebook: any,
    psId: number,
  ): boolean {
    const payload = this.getEvaluationPayloadForProblemStatement(psId);
    if (!payload?.pvc) {
      return false;
    }

    const evaluatePvc = payload.pvc.trim().toLowerCase();
    const evalBase = evaluatePvc.replace(/-pvc$/i, '');

    const rawPvc = (
      notebook?.pvcName ??
      notebook?.pvc ??
      notebook?.pvc_name ??
      ''
    )
      .toString()
      .trim()
      .toLowerCase();
    if (rawPvc) {
      const nbBase = rawPvc.replace(/-pvc$/i, '');
      if (rawPvc === evaluatePvc || nbBase === evalBase) {
        return true;
      }
      if (
        evalBase.length >= 8 &&
        (rawPvc.includes(evalBase) || nbBase.includes(evalBase))
      ) {
        return true;
      }
    }

    const image = (
      notebook?.imageName ??
      notebook?.image ??
      notebook?.image_name ??
      ''
    )
      .toString()
      .toLowerCase();
    const marker =
      psId === 1
        ? 'nha-ps1'
        : psId === 2
          ? 'nha-ps2'
          : psId === 3
            ? 'nha-ps3'
            : '';
    if (marker && image.includes(marker)) {
      return true;
    }

    const nbName = (notebook?.name ?? notebook?.notebookName ?? '')
      .toString()
      .trim()
      .toLowerCase();
    if (nbName && evalBase) {
      if (nbName === evalBase) {
        return true;
      }
      if (evalBase.startsWith(nbName)) {
        return true;
      }
    }

    return false;
  }

  private isNotebookRunning(notebook: any): boolean {
    const status = (
      notebook?.status ||
      notebook?.state ||
      notebook?.phase ||
      ''
    )
      .toString()
      .trim()
      .toLowerCase();
    return status === 'running';
  }

  private resolveNotebookName(notebook: any): string {
    const candidate =
      notebook?.notebookName ||
      notebook?.name ||
      notebook?.notebook_name ||
      notebook?.display_name ||
      notebook?.id ||
      '';
    const name = String(candidate || '').trim();
    return name || 'the running notebook';
  }

  difficultyClass(level: string): string {
    return (
      {
        Beginner: 'tag--beginner',
        Intermediate: 'tag--intermediate',
        Advanced: 'tag--advanced',
      }[level] ?? ''
    );
  }

  setSection(section: 'problems' | 'masterclasses'): void {
    this.activeSection = section;
  }

  private async checkLoginStatus(): Promise<void> {
    try {
      this.isLoggedIn = await this.keycloak.isLoggedIn();
    } catch {
      this.isLoggedIn = false;
    }
  }
}
